import React, { useCallback, useMemo, useState } from 'react';
import type { LayoutChangeEvent, TextProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { BodyText } from '@garnish/core';

import { BrandIcon } from '../BrandIcon';

// ─────────────────────────────────────────────────────────────────────────────

export const ProgressBar = (props: ProgressBarProps) => {
  const { nodes, maxFontSizeMultiplier } = props;

  const { nodeWidth, measureNodes } = useProgressBarMeasurement(nodes.length);

  const lastCompletedNodeIndex = nodes.reduce(
    (lastCompletedIndex, node, index) => (node.completed ? index : lastCompletedIndex),
    -1,
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const renderProgressBarNode = useCallback(
    (node: ProgressBarNodeType, index: number) => (
      <ProgressBarNode
        key={node.id}
        node={node}
        width={nodeWidth}
        maxFontSizeMultiplier={maxFontSizeMultiplier}
        isActive={index === lastCompletedNodeIndex}
      />
    ),
    [lastCompletedNodeIndex, maxFontSizeMultiplier, nodeWidth],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View testID="sg-progress-bar" style={styles.progressBar} onLayout={measureNodes}>
      {nodes.map(renderProgressBarNode)}
    </View>
  );
};

const ProgressBarNode = (props: ProgressBarNodeProps) => {
  const { node, width, maxFontSizeMultiplier, isActive } = props;

  const { id, label, completed, icon, variation = 'hot-spots' } = node;

  // ─── Derived ──────────────────────────────────────────────────────────────

  const dynamicStyle = useStyle(() => ({ width }));
  const lineStyle = completed ? styles.completedLine : styles.pendingLine;

  const circleStyle = completed ? styles.completedCircle : styles.pendingCircle;
  const activeIcon = isActive ? icon : undefined;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={[dynamicStyle, styles.node]} testID={`sg-progress-bar-node-${id}`}>
      {/* NOTE: The track keeps the dot's footprint for every node, so a larger
                icon overflows it instead of pushing its label below the others. */}
      <View style={styles.track}>
        <View style={[styles.absoluteLine, lineStyle]} />

        {variation === 'hot-spots' && activeIcon ? (
          <BrandIcon
            alt={activeIcon.alt}
            src={activeIcon.src}
            width={activeIcon.width}
            height={activeIcon.height}
            style={styles.absoluteCircle}
          />
        ) : null}

        {variation === 'hot-spots' && !activeIcon ? (
          <View style={[styles.absoluteCircle, circleStyle]} />
        ) : null}
      </View>

      <BodyText
        style={styles.label}
        sizeMatch={['12']}
        maxFontSizeMultiplier={maxFontSizeMultiplier}
      >
        {label}
      </BodyText>
    </View>
  );
};

const useProgressBarMeasurement = (nodeCount: number) => {
  // ─── State ────────────────────────────────────────────────────────────────

  const [progressBarWidth, setProgressBarWidth] = useState(640);

  // ─── Derived ──────────────────────────────────────────────────────────────

  const nodeWidth = useMemo(() => progressBarWidth / nodeCount, [nodeCount, progressBarWidth]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const measureNodes = useCallback((event: LayoutChangeEvent) => {
    setProgressBarWidth(event.nativeEvent.layout.height);
  }, []);

  // ───────────────────────────────────────────────────────────────────────────

  return { nodeWidth, measureNodes };
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  progressBar: {
    flex: 1,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  node: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  track: {
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    height: 12,
  },
  absoluteLine: {
    position: 'absolute',
    left: 0,
    right: 0,
  },
  absoluteCircle: {
    position: 'absolute',
  },
  completedLine: {
    backgroundColor: theme.colors.GREEN_2,
    height: 1,
  },
  pendingLine: {
    height: 1,
    backgroundColor: theme.colors.NEUTRAL_3,
  },
  completedCircle: {
    backgroundColor: theme.colors.GREEN_2,
    borderRadius: 12,
    width: 12,
    height: 12,
  },
  pendingCircle: {
    backgroundColor: theme.colors.NEUTRAL_7,
    borderColor: theme.colors.GRAY,
    borderWidth: 1,
    borderRadius: 12,
    width: 12,
    height: 12,
  },
  label: {
    marginTop: theme.spacing['2'],
    color: theme.colors.NEUTRAL_2,
    textAlign: 'center',
    lineHeight: 16,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ProgressBarProps = {
  nodes: readonly ProgressBarNodeType[];
  maxFontSizeMultiplier?: TextProps['maxFontSizeMultiplier'];
};

type ProgressBarNodeProps = {
  node: ProgressBarNodeType;
  width: number;
  maxFontSizeMultiplier?: TextProps['maxFontSizeMultiplier'];
  isActive: boolean;
};

export type ProgressBarNodeType = {
  id: string;
  label: string;
  completed: boolean;
  variation?: 'linear' | 'hot-spots';

  /**
   * Replaces the progress dot while the node is the last completed one.
   */
  icon?: {
    src: string;
    alt: string | undefined;
    width: number;
    height: number;
  };
};
